import React, { useState, forwardRef, useEffect } from "react";
import {
  Alert as MuiAlert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";

// Validation schema
const validationSchema = yup.object({
  username: yup
    .string(
      Labels.getLabel(
        "createUser.validation.enterUsername",
        "Enter the username"
      )
    )
    .required(
      Labels.getLabel(
        "createUser.validation.usernameRequired",
        "Username is required"
      )
    )
    .min(
      3,
      Labels.getLabel(
        "createUser.validation.usernameMinLength",
        "Username must be at least 3 characters"
      )
    )
    .matches(
      /^[a-zA-Z0-9_.@-]+$/,
      Labels.getLabel(
        "createUser.validation.usernameFormat",
        "Username can only contain letters, numbers, dots, hyphens, underscores and @ symbol"
      )
    ),
  email: yup
    .string(
      Labels.getLabel(
        "createUser.validation.enterEmail",
        "Enter the email address"
      )
    )
    .email(
      Labels.getLabel(
        "createUser.validation.validEmail",
        "Enter a valid email address"
      )
    )
    .required(
      Labels.getLabel(
        "createUser.validation.emailRequired",
        "Email is required"
      )
    ),
  firstName: yup
    .string(
      Labels.getLabel(
        "createUser.validation.enterFirstName",
        "Enter the first name"
      )
    )
    .min(
      2,
      Labels.getLabel(
        "createUser.validation.firstNameMinLength",
        "First name must be at least 2 characters"
      )
    ),
  lastName: yup
    .string(
      Labels.getLabel(
        "createUser.validation.enterLastName",
        "Enter the last name"
      )
    )
    .min(
      2,
      Labels.getLabel(
        "createUser.validation.lastNameMinLength",
        "Last name must be at least 2 characters"
      )
    ),
  userType: yup
    .string()
    .required(
      Labels.getLabel(
        "createUser.validation.userTypeRequired",
        "User type is required"
      )
    ),
  location: yup
    .array()
    .min(
      1,
      Labels.getLabel(
        "createUser.validation.locationRequired",
        "At least one location is required"
      )
    )
    .required(
      Labels.getLabel(
        "createUser.validation.locationRequired",
        "Location is required"
      )
    ),
});

export default function CreateUser({ open, setOpen, employerId }) {
  // Component state
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Subdepartment selection state
  const [selectedSubDepartment, setSelectedSubDepartment] = useState("");

  // Location selection state
  const [selectedLocation, setSelectedLocation] = useState([]);

  // CSV Location selection state
  const [selectedCsvLocation, setSelectedCsvLocation] = useState(null);

  // Mode selection state (0 = Individual, 1 = CSV Upload)
  const [creationMode, setCreationMode] = useState(0);

  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileError, setCsvFileError] = useState("");
  const [csvResults, setCsvResults] = useState(null); // Store detailed CSV results
  const [showCsvResults, setShowCsvResults] = useState(false);
  const [csvResultsFilter, setCsvResultsFilter] = useState("all"); // 'all', 'success', 'failed'

  const handleModeChange = (event, newValue) => {
    setCreationMode(newValue);
    // Reset form when switching modes
    formik.resetForm();
    setEmailError("");
    setUsernameError("");
    setCsvFile(null);
    setCsvFileError("");
    setSelectedCsvLocation(null);
  };

  // Handle CSV file selection
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    setCsvFileError("");

    if (!file) {
      setCsvFile(null);
      return;
    }

    // Basic file validation
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvFileError(
        Labels.getLabel(
          "createUser.csv.validation.invalidType",
          "Please select a CSV file"
        )
      );
      setCsvFile(null);
      return;
    }

    // File size validation (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setCsvFileError(
        Labels.getLabel(
          "createUser.csv.validation.fileTooLarge",
          "File size must be less than 5MB"
        )
      );
      setCsvFile(null);
      return;
    }

    setCsvFile(file);
  };

  // Handle CSV form submission
  const handleCsvSubmit = () => {
    if (!csvFile) {
      setCsvFileError(
        Labels.getLabel(
          "createUser.csv.validation.noFile",
          "Please select a CSV file"
        )
      );
      return;
    }

    if (!selectedCsvLocation) {
      setCsvFileError(
        Labels.getLabel(
          "createUser.csv.validation.noLocation",
          "Please select a location"
        )
      );
      return;
    }

    csvUploadMutation.mutate({
      file: csvFile,
      employerId: selectedSubDepartment || employerId,
      locationId: selectedCsvLocation?.id,
    });
  };

  // Generate and download CSV template
  const downloadCsvTemplate = () => {
    const csvContent = `username,email,firstName,lastName,userType
john.doe,john.doe@example.com,John,Doe,admin
jane.smith,jane.smith@example.com,Jane,Smith,user`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "user_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch user types from API
  const {
    data: userTypesData,
    isLoading: isLoadingUserTypes,
    isError: isErrorUserTypes,
  } = useQuery(
    ["userTypes", employerId],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL +
          `/employer/attributes/RMC_USER_TYPES_FOR_CREATION?employerId=${employerId}&fallbackToSystem=true`
      );
      return response.data;
    },
    {
      enabled: open && !!employerId, // Only fetch when dialog is open AND employerId exists
      retry: 2, // Retry twice before giving up
      retryDelay: 1000, // Wait 1 second between retries
    }
  );

  // Fetch subdepartments for the selected employer
  const { data: subDepartmentsData } = useQuery(
    ["subDepartments", employerId],
    async () => {
      if (!employerId) return { data: [] };
      const response = await HttpService.getAxiosClient().get(
        window.API_URL +
          `/subdepartment/options?companyId=${employerId}`
      );
      return response;
    },
    {
      enabled: open && !!employerId,
    }
  );

  // Fetch locations for the selected employer
  const { data: locationsData, isLoading: isLoadingLocations } = useQuery(
    ["locations", employerId],
    async () => {
      if (!employerId) return [];
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/locations?employerId=${employerId}`
      );
      // Add "ANY - All Locations" option with id -1
      const anyLocation = {
        id: -1,
        description: "ANY - All Locations",
        address_line_1: "",
        city: "",
        state: "",
      };
      return [anyLocation, ...response.data];
    },
    {
      enabled: open && !!employerId,
    }
  );

  // Alert component for notifications
  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  // Create user mutation
  const createUserMutation = useMutation(
    (userData) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/employer/user`,
        userData
      );
    },
    {
      onSuccess: (response) => {
        const successMessage =
          response?.data?.message ||
          Labels.getLabel(
            "createUser.success",
            "User created successfully! Login credentials have been sent via email."
          );

        setFeedback({
          open: true,
          type: "success",
          message: successMessage,
          onClose: () => {
            setFeedback({ open: false });
            handleClose();
          },
        });

        // Reset form
        formik.resetForm();
      },
      onError: (error) => {
        let errorMessage = Labels.getLabel(
          "createUser.error.general",
          "An error occurred while creating the user."
        );

        if (error?.response?.status === 409) {
          errorMessage = Labels.getLabel(
            "createUser.error.userExists",
            "Username already exists. Please choose a different username."
          );
        } else if (error?.response?.status === 403) {
          errorMessage = Labels.getLabel(
            "createUser.error.permission",
            "You don't have permission to create users for this employer."
          );
        } else if (error?.response?.status === 400) {
          errorMessage =
            error?.response?.data?.message ||
            Labels.getLabel(
              "createUser.error.invalidInput",
              "Invalid input data. Please check all fields."
            );
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setFeedback({
          open: true,
          type: "error",
          message: errorMessage,
        });
      },
    }
  );

  // CSV Upload mutation
  const csvUploadMutation = useMutation(
    (csvData) => {
      const formData = new FormData();
      formData.append("csvFile", csvData.file);
      formData.append("employerId", csvData.employerId);
      if (csvData.locationId) {
        formData.append("locationId", csvData.locationId);
      }

      return HttpService.getAxiosClient().post(
        window.API_URL + `/employer/user/csv-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    {
      onSuccess: (response) => {
        const results = response?.data;
        let successMessage;
        let alertType;

        // Store detailed results for display
        setCsvResults(results);

        if (results?.successCount > 0 && results?.errorCount > 0) {
          // Partial success - some users created, some failed
          successMessage = Labels.getLabel(
            "createUser.csv.success.partial",
            `${results.successCount} users created successfully, ${results.errorCount} failed. Check details below.`
          );
          alertType = "warning";
          setShowCsvResults(true); // Show detailed results for partial success
        } else if (
          results?.successCount > 0 &&
          (!results?.errorCount || results.errorCount === 0)
        ) {
          // All successful
          successMessage = Labels.getLabel(
            "createUser.csv.success.all",
            `All ${results.successCount} users created successfully!`
          );
          alertType = "success";
        } else if (
          results?.errorCount > 0 &&
          (!results?.successCount || results.successCount === 0)
        ) {
          // All failed
          successMessage = Labels.getLabel(
            "createUser.csv.error.all",
            `All ${results.errorCount} users failed to be created. See error details below.`
          );
          alertType = "error";
          setShowCsvResults(true); // Show detailed results for all failures
        } else {
          // Fallback
          successMessage = Labels.getLabel(
            "createUser.csv.success.default",
            "CSV processed successfully!"
          );
          alertType = "success";
        }

        setFeedback({
          open: true,
          type: alertType,
          message: successMessage,
          onClose: () => {
            setFeedback({ open: false });
            // Don't auto-close dialog if there are results to show
            if (!showCsvResults) {
              handleClose();
            }
          },
        });

        // Reset CSV file but keep results
        setCsvFile(null);
        setCsvFileError("");
      },
      onError: (error) => {
        let errorMessage = Labels.getLabel(
          "createUser.csv.error.general",
          "An error occurred while processing the CSV file."
        );

        if (error?.response?.status === 400) {
          errorMessage =
            error?.response?.data?.message ||
            Labels.getLabel(
              "createUser.csv.error.invalidFile",
              "Invalid CSV file format or data."
            );
        } else if (error?.response?.status === 413) {
          errorMessage = Labels.getLabel(
            "createUser.csv.error.fileTooLarge",
            "File is too large. Maximum 500 users allowed."
          );
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        setFeedback({
          open: true,
          type: "error",
          message: errorMessage,
        });
      },
    }
  );

  // Form validation and submission
  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      employerId: employerId || "",
      userType: "",
      location: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Clean up data before sending
      const userData = {
        username: values.username.trim(),
        email: values.email.trim().toLowerCase(),
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
        employerId: selectedSubDepartment || values.employerId || employerId,
        userType: values.userType,
        locationIds: values.location.map(loc => loc.id),
      };

      // Remove undefined fields
      Object.keys(userData).forEach((key) => {
        if (userData[key] === undefined || userData[key] === "") {
          delete userData[key];
        }
      });

      createUserMutation.mutate(userData);
    },
  });

  // Real-time email validation
  const [emailError, setEmailError] = useState("");
  const validateEmailRealTime = (email) => {
    if (!email) {
      setEmailError("");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(
        Labels.getLabel(
          "createUser.validation.validEmail",
          "Please enter a valid email address"
        )
      );
    } else {
      setEmailError("");
    }
  };

  // Real-time username validation
  const [usernameError, setUsernameError] = useState("");
  const validateUsernameRealTime = (username) => {
    if (!username) {
      setUsernameError("");
      return;
    }

    if (username.length < 3) {
      setUsernameError(
        Labels.getLabel(
          "createUser.validation.usernameMinLength",
          "Username must be at least 3 characters"
        )
      );
    } else if (!/^[a-zA-Z0-9_.@-]+$/.test(username)) {
      setUsernameError(
        Labels.getLabel(
          "createUser.validation.usernameFormat",
          "Username can only contain letters, numbers, dots, hyphens, underscores and @ symbol"
        )
      );
    } else {
      setUsernameError("");
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (createUserMutation.isLoading || csvUploadMutation.isLoading) return; // Prevent closing while submitting
    setOpen(false);
    formik.resetForm();
    setEmailError("");
    setUsernameError("");
    setCsvFile(null);
    setCsvFileError("");
    setCsvResults(null);
    setShowCsvResults(false);
    setCsvResultsFilter("all");
    setCreationMode(0);
    setSelectedSubDepartment(""); // Reset subdepartment selection
    setSelectedLocation([]); // Reset location selection
    setSelectedCsvLocation(null); // Reset CSV location selection
  };

  // Handle feedback close
  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      formik.resetForm();
      setEmailError("");
      setUsernameError("");
      formik.setFieldValue("userType", "");
      setCreationMode(0); // Reset to individual mode
      setCsvFile(null);
      setCsvFileError("");
      setSelectedSubDepartment(""); // Reset subdepartment selection
      setSelectedLocation([]); // Reset location selection
      setSelectedCsvLocation(null); // Reset CSV location selection
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only depend on open state to avoid infinite loop

  // Update employerId when prop changes
  useEffect(() => {
    if (employerId && formik.values.employerId !== employerId) {
      formik.setFieldValue("employerId", employerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);

  // Show loading dialog while fetching user types
  if (open && isLoadingUserTypes) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" mt={3} color="text.secondary">
              {Labels.getLabel(
                "createUser.loadingConfiguration",
                "Loading user configuration..."
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={1}>
              {Labels.getLabel(
                "createUser.loadingConfigurationSubtext",
                "This will only take a moment"
              )}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error dialog if failed to load user types
  if (open && isErrorUserTypes) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel("createUser.error", "Error")}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Typography color="error" gutterBottom fontWeight="medium">
            {Labels.getLabel(
              "createUser.configurationLoadError",
              "Failed to load user configuration"
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Labels.getLabel(
              "createUser.configurationLoadErrorDetail",
              "Please try again or contact support if the problem persists."
            )}
          </Typography>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose} variant="contained" color="primary">
              {Labels.getLabel("createUser.button.close", "Close")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Show warning dialog if no user types are configured
  if (
    open &&
    !isLoadingUserTypes &&
    !isErrorUserTypes &&
    !userTypesData?.value
  ) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel("createUser.warning", "Configuration Required")}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Typography gutterBottom fontWeight="medium">
            {Labels.getLabel(
              "createUser.noUserTypesConfigured",
              "No user types are configured for this employer."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Labels.getLabel(
              "createUser.contactAdmin",
              "Please contact an administrator to configure user types before creating users."
            )}
          </Typography>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose} variant="contained" color="primary">
              {Labels.getLabel("createUser.button.close", "Close")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Show full form only when data is ready
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={createUserMutation.isLoading}
      >
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={formik.handleSubmit}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonAddIcon sx={{ color: "white" }} />
              <Typography variant="h6" sx={{ color: "white" }}>
                {Labels.getLabel("createUser.dialog.title", "Create New User")}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              disabled={createUserMutation.isLoading}
              aria-label="close"
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent sx={{ padding: 3 }}>
            {/* Mode Selection Tabs */}
            <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={creationMode}
                onChange={handleModeChange}
                aria-label="user creation mode"
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    color: "#0d7f81",
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    color: "#0d7f81 !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#0d7f81",
                  },
                }}
              >
                <Tab
                  icon={<PersonAddIcon />}
                  label={Labels.getLabel(
                    "createUser.mode.individual",
                    "Individual User"
                  )}
                  iconPosition="start"
                />
                <Tab
                  icon={<UploadFileIcon />}
                  label={Labels.getLabel(
                    "createUser.mode.csv",
                    "Multiple Users (CSV)"
                  )}
                  iconPosition="start"
                />
              </Tabs>
            </Box>
            {creationMode === 0 ? (
              // Individual User Form
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {Labels.getLabel(
                    "createUser.dialog.description",
                    "Create a new user account. A temporary password will be automatically generated and sent to the user's email address."
                  )}
                </Typography>

                <Grid container spacing={2}>
                  {/* Username - Required */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="username"
                      label={Labels.getLabel(
                        "createUser.form.username",
                        "Username *"
                      )}
                      value={formik.values.username}
                      onChange={(e) => {
                        formik.handleChange(e);
                        validateUsernameRealTime(e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        (formik.touched.username &&
                          Boolean(formik.errors.username)) ||
                        Boolean(usernameError)
                      }
                      helperText={
                        (formik.touched.username && formik.errors.username) ||
                        usernameError ||
                        Labels.getLabel(
                          "createUser.form.usernameHelper",
                          "Unique identifier for login"
                        )
                      }
                      disabled={createUserMutation.isLoading}
                      inputProps={{
                        "aria-label": "Username",
                        maxLength: 50,
                      }}
                    />
                  </Grid>

                  {/* Email - Required */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="email"
                      label={Labels.getLabel(
                        "createUser.form.email",
                        "Email Address *"
                      )}
                      type="email"
                      value={formik.values.email}
                      onChange={(e) => {
                        formik.handleChange(e);
                        validateEmailRealTime(e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        (formik.touched.email &&
                          Boolean(formik.errors.email)) ||
                        Boolean(emailError)
                      }
                      helperText={
                        (formik.touched.email && formik.errors.email) ||
                        emailError ||
                        Labels.getLabel(
                          "createUser.form.emailHelper",
                          "Login credentials will be sent here"
                        )
                      }
                      disabled={createUserMutation.isLoading}
                      inputProps={{
                        "aria-label": "Email address",
                        maxLength: 100,
                      }}
                    />
                  </Grid>

                  {/* First Name - Optional */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="firstName"
                      label={Labels.getLabel(
                        "createUser.form.firstName",
                        "First Name"
                      )}
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.firstName &&
                        Boolean(formik.errors.firstName)
                      }
                      helperText={
                        (formik.touched.firstName && formik.errors.firstName) ||
                        Labels.getLabel("createUser.form.optional", "Optional")
                      }
                      disabled={createUserMutation.isLoading}
                      inputProps={{
                        "aria-label": "First name",
                        maxLength: 50,
                      }}
                    />
                  </Grid>

                  {/* Last Name - Optional */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="lastName"
                      label={Labels.getLabel(
                        "createUser.form.lastName",
                        "Last Name"
                      )}
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.lastName &&
                        Boolean(formik.errors.lastName)
                      }
                      helperText={
                        (formik.touched.lastName && formik.errors.lastName) ||
                        Labels.getLabel("createUser.form.optional", "Optional")
                      }
                      disabled={createUserMutation.isLoading}
                      inputProps={{
                        "aria-label": "Last name",
                        maxLength: 50,
                      }}
                    />
                  </Grid>

                  {/* User Type - Required */}
                  {userTypesData?.value && (
                    <Grid item xs={12}>
                      <FormControl
                        component="fieldset"
                        error={
                          formik.touched.userType &&
                          Boolean(formik.errors.userType)
                        }
                        disabled={createUserMutation.isLoading}
                        fullWidth
                        required
                      >
                        <FormLabel component="legend" required>
                          {Labels.getLabel(
                            "createUser.form.userType",
                            "User Type"
                          )}
                        </FormLabel>
                        <RadioGroup
                          aria-label="user-type"
                          name="userType"
                          value={formik.values.userType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          {userTypesData.value.split(",").map((roleValue) => {
                            const trimmedValue = roleValue.trim();
                            return (
                              <FormControlLabel
                                key={trimmedValue}
                                value={trimmedValue}
                                control={<Radio />}
                                label={Labels.getLabel(
                                  `createUser.userType.${trimmedValue}`,
                                  trimmedValue
                                )}
                              />
                            );
                          })}
                        </RadioGroup>
                        {formik.touched.userType && formik.errors.userType && (
                          <FormHelperText>
                            {formik.errors.userType}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {/* Subdepartment Filter - Optional */}
                  {subDepartmentsData?.data &&
                    subDepartmentsData.data.length > 0 && (
                      <Grid item xs={12}>
                        <Autocomplete
                          fullWidth
                          options={subDepartmentsData.data}
                          getOptionLabel={(option) => option.name || ""}
                          value={
                            subDepartmentsData.data.find(
                              (sd) => sd.id === selectedSubDepartment
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            setSelectedSubDepartment(newValue?.id || "");
                          }}
                          disabled={createUserMutation.isLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={Labels.getLabel(
                                "createUser.form.subdepartment",
                                "Sub-Department (Optional)"
                              )}
                              placeholder={Labels.getLabel(
                                "createUser.form.noSubdepartment",
                                "No Sub-Department (Main Company)"
                              )}
                              helperText={Labels.getLabel(
                                "createUser.form.subdepartmentHelper",
                                "Assign user to a specific sub-department or leave empty for main company"
                              )}
                            />
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                        />
                      </Grid>
                    )}

                  {/* Location Selection - Required (Multiple) */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={Array.isArray(locationsData) ? locationsData : []}
                      getOptionLabel={(option) => {
                        if (option.id === -1) {
                          return option.description;
                        }
                        return `${option.description}, ${option.address_line_1}, ${option.city}, ${option.state}` || "";
                      }}
                      value={formik.values.location}
                      onChange={(event, newValue) => {
                        formik.setFieldValue("location", newValue);
                        setSelectedLocation(newValue);
                      }}
                      onBlur={() => formik.setFieldTouched("location", true)}
                      disabled={createUserMutation.isLoading || isLoadingLocations}
                      loading={isLoadingLocations}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Labels.getLabel(
                            "createUser.form.location",
                            "Locations *"
                          )}
                          placeholder={Labels.getLabel(
                            "createUser.form.selectLocation",
                            "Select one or more locations"
                          )}
                          error={
                            formik.touched.location &&
                            Boolean(formik.errors.location)
                          }
                          helperText={
                            (formik.touched.location && formik.errors.location) ||
                            Labels.getLabel(
                              "createUser.form.locationHelper",
                              "Assign user to one or more locations"
                            )
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingLocations ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </Grid>
                </Grid>

                {/* Form submission info */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>
                      {Labels.getLabel("createUser.note", "Note:")}
                    </strong>{" "}
                    {Labels.getLabel(
                      "createUser.passwordNote",
                      "A temporary password will be automatically generated and sent to the provided email address. The user will be required to change this password on first login."
                    )}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // CSV Upload Form
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {Labels.getLabel(
                    "createUser.csv.description",
                    "Upload a CSV file to create multiple users at once. Maximum 500 users per upload."
                  )}
                </Typography>

                {/* Subdepartment Filter for CSV Upload - Optional */}
                {subDepartmentsData?.data &&
                  subDepartmentsData.data.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Autocomplete
                        fullWidth
                        options={subDepartmentsData.data}
                        getOptionLabel={(option) => option.name || ""}
                        value={
                          subDepartmentsData.data.find(
                            (sd) => sd.id === selectedSubDepartment
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setSelectedSubDepartment(newValue?.id || "");
                        }}
                        disabled={csvUploadMutation.isLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={Labels.getLabel(
                              "createUser.form.subdepartment",
                              "Sub-Department (Optional)"
                            )}
                            placeholder={Labels.getLabel(
                              "createUser.form.noSubdepartment",
                              "No Sub-Department (Main Company)"
                            )}
                            helperText={Labels.getLabel(
                              "createUser.csv.subdepartmentHelper",
                              "All users in the CSV will be assigned to the selected sub-department"
                            )}
                          />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                      />
                    </Box>
                  )}

                {/* Location Filter for CSV Upload - Required */}
                <Box sx={{ mb: 3 }}>
                  <Autocomplete
                    fullWidth
                    options={Array.isArray(locationsData) ? locationsData : []}
                    getOptionLabel={(option) => {
                      if (option.id === -1) {
                        return option.description;
                      }
                      return `${option.description}, ${option.address_line_1}, ${option.city}, ${option.state}` || "";
                    }}
                    value={selectedCsvLocation}
                    onChange={(event, newValue) => {
                      setSelectedCsvLocation(newValue);
                      setCsvFileError(""); // Clear error when location is selected
                    }}
                    disabled={csvUploadMutation.isLoading || isLoadingLocations}
                    loading={isLoadingLocations}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={Labels.getLabel(
                          "createUser.form.location",
                          "Location *"
                        )}
                        placeholder={Labels.getLabel(
                          "createUser.form.selectLocation",
                          "Select a location"
                        )}
                        error={!selectedCsvLocation && csvFileError?.includes("location")}
                        helperText={Labels.getLabel(
                          "createUser.csv.locationHelper",
                          "All users in the CSV will be assigned to the selected location"
                        )}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingLocations ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                  />
                </Box>

                {/* CSV Upload Section */}
                <Box
                  sx={{
                    p: 3,
                    border: csvFile ? "2px solid #0d7f81" : "2px dashed #ddd",
                    borderRadius: 2,
                    textAlign: "center",
                    backgroundColor: csvFile ? "#f0f8f8" : "#fafafa",
                  }}
                >
                  <UploadFileIcon
                    sx={{ fontSize: 48, color: "#0d7f81", mb: 2 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {csvFile
                      ? Labels.getLabel(
                          "createUser.csv.upload.fileSelected",
                          "File Selected"
                        )
                      : Labels.getLabel(
                          "createUser.csv.upload.title",
                          "Upload CSV File"
                        )}
                  </Typography>

                  {csvFile ? (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mb: 2 }}
                      >
                        📄 {csvFile.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 2, display: "block" }}
                      >
                        {(csvFile.size / 1024).toFixed(1)} KB
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ color: "#0d7f81", borderColor: "#0d7f81", mr: 2 }}
                      >
                        {Labels.getLabel(
                          "createUser.csv.changeFile",
                          "Change File"
                        )}
                        <input
                          type="file"
                          accept=".csv"
                          hidden
                          onChange={handleCsvFileChange}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {Labels.getLabel(
                          "createUser.csv.upload.description",
                          "Select a CSV file containing user information"
                        )}
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ color: "#0d7f81", borderColor: "#0d7f81" }}
                      >
                        {Labels.getLabel(
                          "createUser.csv.selectFile",
                          "Select File"
                        )}
                        <input
                          type="file"
                          accept=".csv"
                          hidden
                          onChange={handleCsvFileChange}
                        />
                      </Button>
                    </Box>
                  )}

                  {csvFileError && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                      {csvFileError}
                    </Typography>
                  )}
                </Box>

                {/* Template Download */}
                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {Labels.getLabel(
                      "createUser.csv.template.description",
                      "Don't have a CSV file? Download our template:"
                    )}
                  </Typography>
                  <Button
                    variant="text"
                    sx={{ color: "#0d7f81" }}
                    onClick={downloadCsvTemplate}
                  >
                    {Labels.getLabel(
                      "createUser.csv.template.download",
                      "Download CSV Template"
                    )}
                  </Button>
                </Box>

                {/* CSV Format Info */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>
                      {Labels.getLabel(
                        "createUser.csv.format.title",
                        "CSV Format:"
                      )}
                    </strong>
                    <br />
                    {Labels.getLabel(
                      "createUser.csv.format.description",
                      "Required columns: username, email, userType. Optional: firstName, lastName"
                    )}
                    <br />
                    <strong>
                      {Labels.getLabel("createUser.csv.limits", "Limits:")}
                    </strong>{" "}
                    {Labels.getLabel(
                      "createUser.csv.limits.description",
                      "Maximum 500 users, file size up to 5MB"
                    )}
                  </Typography>
                </Box>

                {/* CSV Results Display */}
                {csvResults && (
                  <Box mt={3}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="h6" style={{ marginRight: "8px" }}>
                        Processing Results
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setShowCsvResults(!showCsvResults)}
                        aria-label={
                          showCsvResults ? "Hide details" : "Show details"
                        }
                      >
                        {showCsvResults ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Box>

                    <Box display="flex" gap={2} mb={1}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`Success: ${csvResults.successCount}`}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        icon={<ErrorIcon />}
                        label={`Failed: ${csvResults.errorCount}`}
                        color="error"
                        variant="outlined"
                      />
                    </Box>

                    {showCsvResults &&
                      csvResults.results &&
                      csvResults.results.length > 0 && (
                        <>
                          {/* Filter Controls */}
                          <Box display="flex" gap={1} mb={2}>
                            <Button
                              size="small"
                              variant={
                                csvResultsFilter === "all"
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() => setCsvResultsFilter("all")}
                            >
                              All ({csvResults.results.length})
                            </Button>
                            <Button
                              size="small"
                              variant={
                                csvResultsFilter === "success"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="success"
                              onClick={() => setCsvResultsFilter("success")}
                            >
                              Success ({csvResults.successCount || 0})
                            </Button>
                            <Button
                              size="small"
                              variant={
                                csvResultsFilter === "failed"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="error"
                              onClick={() => setCsvResultsFilter("failed")}
                            >
                              Failed ({csvResults.errorCount || 0})
                            </Button>
                          </Box>

                          <TableContainer
                            component={Paper}
                            style={{ maxHeight: 300 }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Row</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Username</TableCell>
                                  <TableCell>Email</TableCell>
                                  <TableCell>Details</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {csvResults.results
                                  .filter((result) => {
                                    if (csvResultsFilter === "success")
                                      return result.success;
                                    if (csvResultsFilter === "failed")
                                      return !result.success;
                                    return true; // 'all'
                                  })
                                  .map((result, index) => (
                                    <TableRow key={index}>
                                      <TableCell>
                                        {result.rowNumber || index + 1}
                                      </TableCell>
                                      <TableCell>
                                        {result.success ? (
                                          <Chip
                                            icon={<CheckCircleIcon />}
                                            label="Success"
                                            color="success"
                                            size="small"
                                          />
                                        ) : (
                                          <Chip
                                            icon={<ErrorIcon />}
                                            label="Failed"
                                            color="error"
                                            size="small"
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {result.username || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {result.email || "-"}
                                      </TableCell>
                                      <TableCell style={{ maxWidth: "300px" }}>
                                        {result.success ? (
                                          <Typography
                                            variant="body2"
                                            color="success.main"
                                            sx={{ fontStyle: "italic" }}
                                          >
                                            User created successfully
                                          </Typography>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            color="error.main"
                                            sx={{
                                              wordBreak: "break-word",
                                              whiteSpace: "pre-wrap",
                                            }}
                                          >
                                            {result.error ||
                                              "Unknown error occurred"}
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <Box
            sx={{
              padding: 2,
              gap: 1,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button
              onClick={handleClose}
              disabled={
                createUserMutation.isLoading || csvUploadMutation.isLoading
              }
              color="inherit"
            >
              {Labels.getLabel("createUser.button.cancel", "Cancel")}
            </Button>

            {creationMode === 0 ? (
              // Individual User Submit Button
              <Button
                type="submit"
                variant="contained"
                disabled={
                  createUserMutation.isLoading ||
                  !formik.values.username ||
                  !formik.values.email ||
                  !formik.values.userType ||
                  !formik.values.location ||
                  formik.values.location.length === 0 ||
                  Boolean(emailError) ||
                  Boolean(usernameError)
                }
                startIcon={
                  createUserMutation.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PersonAddIcon />
                  )
                }
                sx={{
                  backgroundColor: "#0d7f81",
                  "&:hover": {
                    backgroundColor: "#0a6b6d",
                  },
                }}
              >
                {createUserMutation.isLoading
                  ? Labels.getLabel("createUser.button.creating", "Creating...")
                  : Labels.getLabel(
                      "createUser.button.createUser",
                      "Create User"
                    )}
              </Button>
            ) : (
              // CSV Upload Submit Button
              <Button
                onClick={handleCsvSubmit}
                variant="contained"
                disabled={
                  csvUploadMutation.isLoading ||
                  !csvFile ||
                  !selectedCsvLocation ||
                  Boolean(csvFileError)
                }
                startIcon={
                  csvUploadMutation.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                sx={{
                  backgroundColor: "#0d7f81",
                  "&:hover": {
                    backgroundColor: "#0a6b6d",
                  },
                }}
              >
                {csvUploadMutation.isLoading
                  ? Labels.getLabel("createUser.csv.uploading", "Processing...")
                  : Labels.getLabel(
                      "createUser.csv.processFile",
                      "Process CSV"
                    )}
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose || handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose || handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
}
